<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import { ref, onMounted, nextTick, computed } from "vue";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";
import LeavesAdminTable from "./leaves-admin-table.vue";
import LeavesAdminModal from "@/components/modal/leaves-admin-modal.vue";
import LeavesAdminApplyModal from "@/components/modal/leaves-admin-apply-modal.vue";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const title = "Leaves";
const text = "HR";
const text1 = "Leaves";

const dateRangeInput = ref(null);
const store = useAdminLeaveStore();
const auth = useAuthStore();
const modalRef = ref(null);
const applyModalRef = ref(null);
const selectedRowKeys = ref([]);

const canDecideTeamLeaves = computed(() => {
  return (
    (typeof auth.can === "function" && auth.can("Leaves.Approve")) ||
    !!auth.user?.is_line_manager ||
    (Array.isArray(auth.user?.roles) &&
      auth.user.roles.some((r) =>
        ["Admin", "HR", "Hr"].includes(typeof r === "string" ? r : r?.name)
      ))
  );
});

const canFinalApproveLeaves = computed(() => {
  return (
    (typeof auth.can === "function" && auth.can("Leaves.Approve")) ||
    (Array.isArray(auth.user?.roles) &&
      auth.user.roles.some((r) =>
        ["Admin", "HR", "Hr"].includes(typeof r === "string" ? r : r?.name)
      ))
  );
});

const selfEmployeeId = computed(() =>
  Number(auth.user?.employee?.id || auth.user?.employee_id || 0)
);

const totalPages = computed(() => {
  const t = store.pagination.total || 0;
  const p = store.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

const selectedPendingIds = computed(() =>
  store.items
    .filter((r) => {
      if (!selectedRowKeys.value.includes(r.id)) return false;
      if (canFinalApproveLeaves.value) {
        return r.status === "New" || r.status === "ManagerApproved";
      }
      return r.status === "New";
    })
    .map((r) => r.id)
);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}
function formatCount(n) {
  return n ?? 0;
}

function initDateRange() {
  if (!dateRangeInput.value) return;
  const start = moment().startOf("year");
  const end = moment().endOf("year");

  function setLabel(s, e) {
    dateRangeInput.value.value = `${s.format("DD/MM/YYYY")} - ${e.format("DD/MM/YYYY")}`;
    store.filters.date_from = s.format("YYYY-MM-DD");
    store.filters.date_to = e.format("YYYY-MM-DD");
    reload();
  }

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      autoUpdateInput: false,
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "This Year": [moment().startOf("year"), moment().endOf("year")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    },
    setLabel
  );

  setLabel(start, end);
}

function reload() {
  selectedRowKeys.value = [];
  return Promise.all([store.fetchAll({ page: 1 }), store.fetchStats()]);
}

function goToPage(page) {
  store.fetchAll({ page });
}

async function onApprove(id) {
  await store.approve(id);
  selectedRowKeys.value = selectedRowKeys.value.filter((k) => k !== id);
}

async function onDecline({ id, reason }) {
  await store.decline(id, reason);
  selectedRowKeys.value = selectedRowKeys.value.filter((k) => k !== id);
}

async function onRevoke({ id, reason }) {
  await store.revoke(id, reason);
}

async function bulkApprove() {
  if (!selectedPendingIds.value.length) return;
  const ids = selectedPendingIds.value.filter((id) => {
    const row = store.items.find((r) => r.id === id);
    return !(selfEmployeeId.value > 0 && Number(row?.employee_id) === selfEmployeeId.value);
  });
  if (!ids.length) {
    toast("You cannot approve your own leave. Another approver must review it.", "error");
    return;
  }
  if (ids.length < selectedPendingIds.value.length) {
    toast("Your own leave was left out. Another approver must review it.", "warning");
  }
  await store.bulkApprove(ids);
  selectedRowKeys.value = [];
}

async function bulkDecline() {
  if (!selectedPendingIds.value.length) return;
  modalRef.value?.openBulkDeclineModal(selectedPendingIds.value);
}

async function onBulkDecline({ ids, reason }) {
  await store.bulkDecline(ids, reason);
  selectedRowKeys.value = [];
}

onMounted(async () => {
  await Promise.all([store.fetchAll(), store.fetchStats()]);
  nextTick(initDateRange);
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="mb-2 me-2">
            <button
              class="btn btn-primary d-flex align-items-center"
              v-can="'Leaves.Create'"
              @click="applyModalRef?.open()"
            >
              <i class="ti ti-circle-plus me-2"></i>Apply for Employee
            </button>
          </div>
          <div class="head-icons ms-2">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xl-3 col-md-6">
          <div class="card bg-green-img">
            <div class="card-body d-flex justify-content-between align-items-center">
              <span
                class="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center"
              >
                <i class="ti ti-user-question text-success fs-18"></i>
              </span>
              <div class="text-end">
                <p class="mb-1">Pending Requests</p>
                <h4>{{ formatCount(store.stats.total_pending) }}</h4>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card bg-blue-img">
            <div class="card-body d-flex justify-content-between align-items-center">
              <span
                class="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center"
              >
                <i class="ti ti-user-check text-info fs-18"></i>
              </span>
              <div class="text-end">
                <p class="mb-1">Approved</p>
                <h4>{{ formatCount(store.stats.total_approved) }}</h4>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card bg-yellow-img">
            <div class="card-body d-flex justify-content-between align-items-center">
              <span
                class="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center"
              >
                <i class="ti ti-user-exclamation text-warning fs-18"></i>
              </span>
              <div class="text-end">
                <p class="mb-1">Declined</p>
                <h4>{{ formatCount(store.stats.total_declined) }}</h4>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6">
          <div class="card bg-pink-img">
            <div class="card-body d-flex justify-content-between align-items-center">
              <span
                class="avatar avatar-md rounded-circle bg-white d-flex align-items-center justify-content-center"
              >
                <i class="ti ti-calendar-stats text-pink fs-18"></i>
              </span>
              <div class="text-end">
                <p class="mb-1">Days Booked (range)</p>
                <h4>{{ formatCount(store.stats.days_booked) }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <div class="d-flex align-items-center flex-wrap gap-2">
            <h5 class="mb-0">Leave List</h5>
            <template v-if="selectedPendingIds.length && canDecideTeamLeaves">
              <button
                class="btn btn-sm btn-success"
                @click="bulkApprove"
              >
                Approve / Recommend ({{ selectedPendingIds.length }})
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="bulkDecline"
              >
                Decline ({{ selectedPendingIds.length }})
              </button>
            </template>
          </div>
          <div class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <input
                class="form-control"
                v-model.trim="store.filters.q"
                @keyup.enter="reload()"
                placeholder="Search name/type"
              />
            </div>
            <div class="me-3">
              <select class="form-select" v-model="store.filters.status" @change="reload()">
                <option value="">All Status</option>
                <option value="New">Pending Manager</option>
                <option value="ManagerApproved">Awaiting HR</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <select class="form-select" v-model="store.filters.sort" @change="reload()">
                <option value="recent">Recently Added</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <leaves-admin-table
              :rows="store.items"
              :loading="store.loading"
              :can-decide="canDecideTeamLeaves"
              :can-final-approve="canFinalApproveLeaves"
              :self-employee-id="selfEmployeeId"
              v-model:selected-row-keys="selectedRowKeys"
              @approve="(id) => modalRef?.openApproveModal(store.items.find((r) => r.id === id), canFinalApproveLeaves)"
              @decline="(payload) => modalRef?.openDeclineModal(store.items.find((r) => r.id === payload.id))"
              @revoke="(id) => modalRef?.openRevokeModal(store.items.find((r) => r.id === id))"
            />
          </div>
        </div>

        <div
          class="card-footer d-flex align-items-center justify-content-between flex-wrap gap-2"
        >
          <div>
            Showing page {{ store.pagination.current_page }} of {{ totalPages }}
            <span v-if="store.pagination.total">
              ({{ store.pagination.total }} records)
            </span>
          </div>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="store.pagination.current_page <= 1 || store.loading"
              @click="goToPage(store.pagination.current_page - 1)"
            >
              Prev
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="store.pagination.current_page >= totalPages || store.loading"
              @click="goToPage(store.pagination.current_page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <leaves-admin-modal
    ref="modalRef"
    @approve="onApprove"
    @decline="onDecline"
    @revoke="onRevoke"
    @bulk-decline="onBulkDecline"
  />
  <leaves-admin-apply-modal ref="applyModalRef" />
</template>
